import { classNames } from "primereact/utils";
import styles from "./HandoverNotePin.module.scss";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";

/**
 * NotesIcon defaults to black, which is invisible on the planner's dark glass, and its
 * path pins `color` as an attribute so CSS inheritance cannot reach it. The tone has to
 * be handed in, and it shifts to the alert colour once the day holds something unread.
 */
const ICON_COLOR = "rgba(255, 255, 255, 0.92)";
const ICON_COLOR_UNREAD = "rgb(255, 138, 148)";

interface IHandoverNotePinProps {
  entriesCount: number;
  hasUnread: boolean;
  unreadCount: number;
  previewContent: string | null;
  previewAuthor: string | null;
  onClick: () => void;
}

/**
 * The hand-over thread as it sits pinned above the day's classes: a single tappable
 * card with the newest message clamped to two lines, plus a pulsing dot while the user
 * has not read what a colleague added. Only rendered once the day actually holds
 * something; until then the round button on the search row is the way in.
 */
export const HandoverNotePin = ({
  entriesCount,
  hasUnread,
  unreadCount,
  previewContent,
  previewAuthor,
  onClick,
}: IHandoverNotePinProps) => {
  const remainingCount = entriesCount - 1;

  return (
    <button
      type="button"
      className={classNames(styles.root, { [styles.root_unread]: hasUnread })}
      onClick={onClick}
      aria-label="Отвори бележката за деня"
    >
      <div
        className={classNames(styles.root_icon, {
          [styles.root_icon_unread]: hasUnread,
        })}
      >
        <Icon
          type={IconType.Notes}
          color={hasUnread ? ICON_COLOR_UNREAD : ICON_COLOR}
          width="16px"
          height="16px"
        />
        <RenderWhen condition={hasUnread}>
          <span className={styles.root_dot} aria-label="Непрочетени бележки" />
        </RenderWhen>
      </div>

      <div className={styles.root_body}>
        <div className={styles.root_meta}>
          <span className={styles.root_author}>{previewAuthor}</span>
          <RenderWhen condition={hasUnread}>
            <span className={styles.root_badge}>
              {unreadCount} {unreadCount === 1 ? "нова" : "нови"}
            </span>
          </RenderWhen>
        </div>

        <p className={styles.root_preview}>{previewContent}</p>

        <RenderWhen condition={remainingCount > 0}>
          <span className={styles.root_more}>
            + още {remainingCount}{" "}
            {remainingCount === 1 ? "бележка" : "бележки"}
          </span>
        </RenderWhen>
      </div>

      <Icon
        className={styles.root_chevron}
        type={IconType.RightChevronIcon}
        color={ICON_COLOR}
        width="14px"
        height="14px"
      />
    </button>
  );
};
